import {
  ChangeDetectorRef,
  Directive,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: 'input[app-input], textarea[app-input]',
  exportAs: 'appInput'
})
export class InputDirective implements OnInit, OnChanges, DoCheck, OnDestroy {
  @Input() title?: string;
  @Input() suffixIcon?: TemplateRef<void>;
  @Input() errors?: string | string[] | Record<string, string>;
  @Output() suffixIconClick: EventEmitter<PointerEvent> = new EventEmitter<PointerEvent>();

  constructor(
    @Optional() private control: NgControl,
    private element: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    if ((<HTMLInputElement>e.target).name === 'password' && e.keyCode === 32) {
      e.preventDefault();
    }
  }

  @HostListener('change', ['$event']) onChange(e: any) {
    this.element.nativeElement['value'] = e.target.value;
    this.control.control?.setValue(e.target.value);
  }

  @HostListener('paste', ['$event']) onPaste(e: any) {
    e.target.value = e.clipboardData.getData('text/plain');
    if (e.target.name === 'password') {
      const sanitizedText = e.target.value.replace(/\s/g, '');
      e.target.value = sanitizedText;
      this.element.nativeElement['value'] = sanitizedText;
    }
    this.control.control?.setValue(e.target.value);
    e.preventDefault();
  }

  ngOnInit(): void {
    const element = this.element.nativeElement;
    const inputClassList = Array.from(element.classList);
    element.className =
      'prose prose-text-sm z-10 max-w-none flex-1 border-none bg-transparent p-0 text-neutral-800 focus:text-neutral-900 focus:outline-none focus:ring-0';

    this.renderer.setAttribute(element, 'placeholder', '');

    const inputWrapper = document.createElement('div');
    inputWrapper.classList.add(
      ...inputClassList,
      'relative',
      'flex',
      'gap-2',
      'rounded-xl',
      'border',
      'border-neutral-200',
      'bg-white',
      'px-3',
      'py-4',
      'duration-300',
      'focus-within:border-neutral-900'
    );
    inputWrapper.addEventListener('click', () => element.focus());

    const label = document.createElement('label');
    this.renderer.setAttribute(label, 'for', element.id);
    label.className = 'overflow-hidden text-ellipsis';
    label.innerText = this.title ?? '';

    const requiredMark = document.createElement('span');
    requiredMark.innerText = '*';
    requiredMark.classList.add('font-bold', 'text-primary-500');

    const divLabel = document.createElement('div');
    divLabel.classList.add(
      'prose',
      'prose-text-sm',
      'absolute',
      'top-4',
      'flex',
      'max-w-[calc(100%-0.75rem*2-0.5rem-20px)]',
      'gap-2',
      'whitespace-nowrap',
      'font-regular',
      'text-neutral-800',
      'duration-300'
    );

    this.renderer.appendChild(divLabel, label);
    this.renderer.appendChild(divLabel, requiredMark);

    const controlWrapper = document.createElement('div');
    controlWrapper.className = 'flex flex-col gap-1.5';
    element.replaceWith(controlWrapper);

    this.renderer.appendChild(inputWrapper, element);
    this.renderer.appendChild(inputWrapper, divLabel);
    this.renderer.appendChild(controlWrapper, inputWrapper);

    if (this.suffixIcon) {
      this.addIcon(inputWrapper, this.suffixIcon);
    }

    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const element = this.element.nativeElement;
    const inputWrapper = element.parentElement;
    if (!inputWrapper) return;

    if (changes['title'] && !changes['title'].firstChange) {
      const title: HTMLLabelElement | null = inputWrapper.querySelector('label[for]');
      if (title) {
        title.innerText = this.title ?? '';
      }
    }

    if (changes['suffixIcon'] && !changes['suffixIcon'].firstChange) {
      inputWrapper.querySelector('app-icon')?.replaceWith();
      this.addIcon(inputWrapper, changes['suffixIcon'].currentValue);
    }
  }
  ngOnDestroy() {
    this.element.nativeElement.parentElement?.parentElement?.remove();
  }
  ngDoCheck(): void {
    if (!this.control) return;
    const element = this.element.nativeElement;
    const inputWrapper = element.parentElement;
    if (!inputWrapper) return;

    const errorMessages: string[] = [];
    if (this.control.errors && this.control.touched) {
      inputWrapper.classList.add('!border-primary-600');

      if (typeof this.errors === 'string') errorMessages.push(this.errors);
      else if (Array.isArray(this.errors)) errorMessages.push(...this.errors);
      else if (this.errors) {
        for (const key in this.errors) {
          if (!this.control.hasError(key)) continue;
          errorMessages.push(this.errors[key]);
          break;
        }
      }
    } else {
      inputWrapper.classList.remove('!border-primary-600');
    }

    const controlWrapper = inputWrapper.parentElement;
    if (controlWrapper) {
      if (controlWrapper.childElementCount >= 2) {
        this.renderer.removeChild(controlWrapper, controlWrapper?.children[1]);
      }
      if (errorMessages.length > 0) {
        const errorsWrapper = document.createElement('div');
        errorsWrapper.className = 'flex flex-col px-3';
        for (const message of errorMessages) {
          const messageEl = document.createElement('span');
          messageEl.className = 'prose prose-text-xs text-primary-600';
          messageEl.innerHTML = message;
          this.renderer.appendChild(errorsWrapper, messageEl);
        }

        this.renderer.appendChild(controlWrapper, errorsWrapper);
      }
    }
    this.cdr.markForCheck();
  }

  private addIcon(parent: HTMLElement, icon: TemplateRef<void>): void {
    const embeddedView = icon.createEmbeddedView();
    embeddedView.detectChanges();
    const iconEl = embeddedView.rootNodes[0];
    iconEl.classList.add('cursor-pointer');
    this.renderer.appendChild(parent, iconEl);

    iconEl.addEventListener('click', (event: PointerEvent) => this.suffixIconClick.emit(event));
  }
}
